/**
 * @file   DemandHistogram/index.js
 * @brief  Read-only demand histogram for Match tab showing Want-Listing price distribution.
 *         Displays peak demand label, "YOU" marker at asking price, and sweet spot highlighting.
 *         Does NOT have slider interaction - the SinglePriceSlider is a separate component.
 */
import React, { useMemo } from "react";
import { formatCompactPrice, computeSweetSpotRange } from "../../../../helpers/priceCapUtils";
import "./DemandHistogram.scss";

const NUM_BINS = 30;
const MIN_BAR_HEIGHT_PCT = 2;

/**
 * Compute fixed-scale histogram bins from price data.
 * Pattern adapted from PriceHistogramSlider bin computation.
 */
const computeBins = (prices, rangeMax) => {
  if (!prices || prices.length === 0 || !rangeMax || rangeMax <= 0) return null;

  const binWidth = rangeMax / NUM_BINS;
  const bins = Array.from({ length: NUM_BINS }, (_, i) => ({
    count: 0,
    start: i * binWidth,
    end: (i + 1) * binWidth,
  }));

  prices.forEach((price) => {
    let idx = Math.floor(price / binWidth);
    if (idx >= NUM_BINS) idx = NUM_BINS - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
  });

  return bins;
};

const DemandHistogram = ({
  prices,
  adType,
  askingPrice,
  sliderPrice,
  rangeMin,
  rangeMax,
}) => {
  const bins = useMemo(() => computeBins(prices, rangeMax), [prices, rangeMax]);

  const sweetSpot = useMemo(
    () => computeSweetSpotRange(prices, adType),
    [prices, adType],
  );

  if (!bins) return null;

  const maxCount = Math.max(...bins.map((b) => b.count));
  if (maxCount === 0) return null;

  // Find peak bin index
  let peakIdx = 0;
  bins.forEach((b, i) => {
    if (b.count > bins[peakIdx].count) peakIdx = i;
  });

  // Calculate "YOU" marker position (as percentage of width)
  const youPct = askingPrice > 0
    ? Math.min(Math.max((askingPrice / rangeMax) * 100, 0), 100)
    : null;

  // Calculate slider position for highlight region
  const sliderPct = sliderPrice != null
    ? Math.min(Math.max((sliderPrice / rangeMax) * 100, 0), 100)
    : null;

  return (
    <div className="demand-histogram">
      {/* Peak demand annotation */}
      <div className="demand-histogram__annotations">
        <span className="demand-histogram__peak-label">
          Peak: {formatCompactPrice(bins[peakIdx].start + (bins[peakIdx].end - bins[peakIdx].start) / 2)}
        </span>
      </div>

      {/* Histogram bars */}
      <div className="demand-histogram__chart">
        {bins.map((bin, i) => {
          const heightPct = maxCount > 0
            ? Math.max((bin.count / maxCount) * 100, bin.count > 0 ? MIN_BAR_HEIGHT_PCT : 0)
            : 0;

          // Bar is in sweet spot zone
          const inSweetSpot = sweetSpot
            ? bin.end > sweetSpot.min && bin.start < sweetSpot.max
            : false;

          // Bar is highlighted by slider exploration
          const inSliderZone = sliderPct != null && youPct != null
            ? (sliderPct <= youPct
              ? bin.end > (sliderPrice) && bin.start < askingPrice
              : bin.end > askingPrice && bin.start < sliderPrice)
            : false;

          const isPeak = i === peakIdx;

          let barClass = "demand-histogram__bar";
          if (inSliderZone) barClass += " demand-histogram__bar--explored";
          else if (inSweetSpot) barClass += " demand-histogram__bar--sweet";
          else if (bin.count > 0) barClass += " demand-histogram__bar--default";
          if (isPeak) barClass += " demand-histogram__bar--peak";

          return (
            <div key={i} className="demand-histogram__col">
              <div
                className={barClass}
                style={{ height: `${heightPct}%` }}
              />
            </div>
          );
        })}

        {/* "YOU" marker overlay */}
        {youPct != null && (
          <div
            className="demand-histogram__you-marker"
            style={{ left: `${youPct}%` }}
          >
            <span className="demand-histogram__you-label">YOU</span>
            <div className="demand-histogram__you-line" />
          </div>
        )}
      </div>

      {/* Price range labels */}
      <div className="demand-histogram__axis">
        <span>{formatCompactPrice(rangeMin || 0)}</span>
        <span>{formatCompactPrice(rangeMax)}</span>
      </div>
    </div>
  );
};

export default DemandHistogram;
